<template>
  <v-container>
    <div>
      <v-btn :to="{ path: '/profile/' + $route.params.username }" color="primary"> Back to Profile </v-btn>
      <h1 class="display-2 text-center">{{ $route.params.username }}'s Contacts</h1>
      <br />

      <section v-if="pending.length > 0" class="mb-10">
        <h2>Requests</h2>
        <div
          v-for="item in pending"
          :key="pending.indexOf(item)"
          class="d-flex align-center justify-space-between user"
        >
          <div class="username-and-pic">
            <router-link :to="{ path: '/profile/' + item.username }" class="d-flex align-center userLink">
              <v-img
                class="rounded-circle"
                :src="
                  item.profile_pic || 'https://paradisevalleychristian.org/wp-content/uploads/2017/01/Blank-Profile.png'
                "
                aspect-ratio="1"
                max-height="50"
                max-width="50"
              />
              <h3>{{ item.username }}</h3>
            </router-link>
          </div>
          <div>
            <v-btn color="primary" class="mr-2" @click="addFriendAndUpdateStatus(item.id)" width="120" elevation="0">
              Approve
            </v-btn>
            <v-btn color="danger" @click="removeFriendAndUpdateStatus(item.id)" width="120" outlined> Decline </v-btn>
          </div>
        </div>
      </section>

      <h2>Connections</h2>
      <div
        v-for="(item, index) in friends"
        :key="friends.indexOf(item)"
        class="d-flex align-center justify-space-between user"
      >
        <div class="username-and-pic">
          <router-link :to="{ path: '/profile/' + item.username }" class="d-flex align-center userLink">
            <v-img
              class="rounded-circle"
              :src="
                item.profile_pic || 'https://paradisevalleychristian.org/wp-content/uploads/2017/01/Blank-Profile.png'
              "
              aspect-ratio="1"
              width="50"
              max-height="50"
              max-width="50"
            />
            <h3>{{ item.username }}</h3>
          </router-link>
        </div>

        <div v-if="username && username !== $route.params.username">
          <v-btn
            v-if="sessionUserFriends.includes(item.username) || sessionUserOutgoing.includes(item.username)"
            @click="startRemove(item)"
            :color="hoveredButtonIdx === index ? 'danger' : 'secondary'"
            outlined
            :width="$vuetify.breakpoint.xs ? 36 : 120"
            @mouseenter="hoveredButtonIdx = index"
            @mouseleave="hoveredButtonIdx = null"
            min-width="0"
          >
            <div v-if="hoveredButtonIdx === index">
              <v-icon>mdi-account-minus</v-icon>
              <span v-if="!$vuetify.breakpoint.xs">Remove</span>
            </div>
            <div v-else-if="sessionUserFriends.includes(item.username)">
              <!-- Already friends -->
              <v-icon>mdi-account-check</v-icon>
              <span v-if="!$vuetify.breakpoint.xs">Friends</span>
            </div>
            <div v-else>
              <!-- Friend already requested -->
              <v-icon>mdi-account-clock</v-icon>
              <span v-if="!$vuetify.breakpoint.xs">Requested</span>
            </div>
          </v-btn>

          <!-- Add friend button -->
          <v-btn
            v-else-if="item.username !== username"
            @click="add(item)"
            :width="$vuetify.breakpoint.xs ? 36 : 120"
            min-width="0"
            color="primary"
          >
            <v-icon>mdi-account-plus</v-icon>
            <span v-if="!$vuetify.breakpoint.xs">Add</span>
          </v-btn>
        </div>

        <v-btn
          v-else-if="username"
          @click="startRemove(item)"
          :width="$vuetify.breakpoint.xs ? 36 : 120"
          min-width="0"
          color="danger"
          outlined
        >
          <v-icon>mdi-account-minus</v-icon>
          <span v-if="!$vuetify.breakpoint.xs">Remove</span>
        </v-btn>
      </div>
    </div>

    <!-- Confirm delete friend -->
    <v-dialog v-model="removeDialog.active" max-width="500">
      <v-card>
        <p class="mx-4 pt-4">Are you sure you want to remove {{ removeDialog.username }} from your friends?</p>

        <v-card-actions>
          <v-btn @click="removeDialog.active = false"> No </v-btn>
          <v-spacer></v-spacer>
          <v-btn color="primary" @click="remove(removeDialog.userId, removeDialog.username)"> Yes </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import friends from "../mixins/friends.js";
import { mapMutations } from "vuex";

export default {
  name: "ContactsPage",
  mixins: [friends],

  data: function () {
    return {
      search: "",
      friends: [],
      pending: [],

      sessionUserFriends: [],
      sessionUserOutgoing: [],

      hoveredButtonIdx: null, // used for "remove friend" option

      removeDialog: {
        active: false,
        username: "",
        userId: null,
      },
    };
  },

  computed: {
    listItemCols() {
      switch (this.$vuetify.breakpoint.name) {
        case "xs":
          return 4;
        default:
          return 2;
      }
    },
    username: function () {
      return this.$store.state.username;
    },
  },

  mounted: async function () {
    let friendsPromise = this.getFriends(this.$route.params.username);
    let incomingRequestsPromise = [];
    let sessionUserFriendsPromise = [];
    let sessionUserOutgoingPromise = [];

    if (this.$route.params.username === this.$store.state.username) {
      incomingRequestsPromise = this.getIncomingRequests();
    } else if (this.$store.state.username) {
      sessionUserFriendsPromise = this.getFriends(this.username);
      sessionUserOutgoingPromise = this.getOutgoingRequests();
    }

    [this.friends, this.pending, this.sessionUserFriends, this.sessionUserOutgoing] = await Promise.all([
      friendsPromise,
      incomingRequestsPromise,
      sessionUserFriendsPromise,
      sessionUserOutgoingPromise,
    ]);

    this.sessionUserFriends = this.sessionUserFriends.map((item) => item.username);
    this.sessionUserOutgoing = this.sessionUserOutgoing.map((user) => user.username);
  },

  methods: {
    ...mapMutations(["showSnackbar"]),

    add: async function (user) {
      let success = await this.addFriendById(user.id);
      if (success) {
        this.sessionUserOutgoing.push(user.username);
      } else {
        this.showSnackbar({
          text: "An error occurred",
          error: true,
        });
      }
    },

    startRemove: function (user) {
      this.removeDialog.active = true;
      this.removeDialog.username = user.username;
      this.removeDialog.userId = user.id;
    },

    remove: async function (userId, username) {
      let success = false;
      try {
        success = await this.removeFriendById(userId);
      } catch (err) {
        success = false;
      }
      if (success) {
        if (this.username === this.$route.params.username) {
          let friendIdx = this.friends.findIndex((user) => user.username === username);
          if (friendIdx >= 0) this.friends.splice(friendIdx, 1);
        } else {
          let sessionUserFriendsIdx = this.sessionUserFriends.indexOf(username);
          if (sessionUserFriendsIdx >= 0) this.sessionUserFriends.splice(sessionUserFriendsIdx, 1);
          let sessionUserOutgoingIdx = this.sessionUserOutgoing.indexOf(username);
          if (sessionUserOutgoingIdx >= 0) this.sessionUserOutgoing.splice(sessionUserOutgoingIdx, 1);
        }
      } else {
        this.showSnackbar({
          text: "An error occurred",
          error: true,
        });
      }

      this.removeDialog.username = "";
      this.removeDialog.active = false;
    },

    async addFriendAndUpdateStatus(userId) {
      try {
        await this.addFriendById(userId);
        let pendingIdx = this.pending.findIndex((user) => user.id === userId);
        let friend = this.pending.splice(pendingIdx, 1)[0];
        this.friends.push(friend);
      } catch (err) {
        this.showSnackbar({
          text: "An error occurred",
          error: true,
        });
      }
    },

    async removeFriendAndUpdateStatus(userId) {
      try {
        await this.removeFriendById(userId);
        let pendingIdx = this.pending.findIndex((user) => user.id === userId);
        this.pending.splice(pendingIdx, 1)[0];
      } catch (err) {
        this.showSnackbar({
          text: "An error occurred",
          error: true,
        });
      }
    },

    /**
     * Returns a user's incoming friend requests only if properly authenticated
     * @returns {Promise<{id: number, profile_pic: string, username: string}[]>}
     */
    getIncomingRequests: async function () {
      let response = await this.$axios.get("/incoming-requests");
      return response.data;
    },

    getOutgoingRequests: async function () {
      let response = await this.$axios.get("/outgoing-requests");
      return response.data;
    },

    /**
     * Get an array of friends for a specific user.
     * @param {string} username
     * @returns {Promise<{id: number, profile_pic: string, username: string}[]>}
     * an array of user objects containing usernames, profile picture urls, etc.
     */
    getFriends: async function (username) {
      let response = await this.$axios.get("/contacts", {
        params: { user: username },
      });
      return response.data;
    },
  },
};
</script>

<style scoped>
/* Used for usernames */
h3 {
  font-size: 1.5em;
  padding-left: 5px;

  max-width: calc(100% - 50px);
  overflow-wrap: break-word;
}
h2 {
  border-bottom: 1px solid gray; /* For divider in users list */
}

.user {
  border-bottom: 1px solid gray;
  padding: 3px 0;

  width: 100%;
}
.username-and-pic {
  max-width: calc(100% - 36px);
}
router-link {
  height: 56px;
  width: 100%;
}

/* Used to combat some vuetify padding */
.container {
  padding: 0;
}
</style>
